/**
 * Mover uma aula à mão, travar, destravar — e a cesta. P14 (§24.11).
 *
 * O que ela faz no URÂNIA, turma por turma: *"coloco exatamente do jeito que
 * encontrei na mão, e dá certinho"*. E quando o lugar não serve, *"ele me dá
 * esses avisos"*. Aqui os avisos vêm ANTES de escolher: cada horário da turma
 * aparece já marcado.
 *
 * - **livre** → um clique, e a aula vai travada;
 * - **aviso** (professor não liberou, aula não pode ali) → pede motivo; a aula
 *   vai "na cesta", com a pendência aberta em negociação;
 * - **bloqueado** (turma ou professor já em aula) → não deixa, e diz por quê.
 */

import { useEffect, useMemo, useState } from 'react'

import { ErroApi, movimento as api, type AlocacaoGrade, type OpcoesDeMovimento } from '../api'
import { Icon } from '../components/Icon'
import { Painel } from '../components/Painel'

const DIA = ['', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']
const hora = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`

type Horario = OpcoesDeMovimento['horarios'][number]

export function Mover({
  projeto,
  cenario,
  aula,
  aoMudar,
  aoFechar,
}: {
  projeto: string
  cenario: string
  aula: AlocacaoGrade
  aoMudar: () => void
  aoFechar: () => void
}) {
  const [opcoes, setOpcoes] = useState<OpcoesDeMovimento | null>(null)
  const [escolhido, setEscolhido] = useState<Horario | null>(null)
  const [motivo, setMotivo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  const ler = () =>
    api
      .opcoes(projeto, cenario, aula.id)
      .then((o) => (setOpcoes(o), setErro(null)))
      .catch((e) => setErro(e instanceof ErroApi ? e.message : 'não foi possível ler os horários'))
  useEffect(() => {
    void ler()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projeto, cenario, aula.id])

  // A grade da turma: dias nas colunas, horários nas linhas.
  const { dias, inicios, em } = useMemo(() => {
    const hs = opcoes?.horarios ?? []
    const dias = [...new Set(hs.map((h) => h.dia))].sort((a, b) => a - b)
    const inicios = [...new Set(hs.map((h) => h.inicio))].sort((a, b) => a - b)
    const mapa = new Map(hs.map((h) => [`${h.dia}/${h.inicio}`, h]))
    return { dias, inicios, em: (d: number, i: number) => mapa.get(`${d}/${i}`) }
  }, [opcoes])

  async function enviar(mudanca: Parameters<typeof api.mover>[3]) {
    setEnviando(true)
    try {
      await api.mover(projeto, cenario, aula.id, mudanca)
      aoMudar()
      setEscolhido(null)
      setMotivo('')
      await ler()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'não foi possível mover')
    } finally {
      setEnviando(false)
    }
  }

  function clicar(h: Horario) {
    setErro(null)
    if (h.atual) return
    const duro = h.avisos.find((a) => a.duro)
    if (duro) {
      setErro(duro.mensagem)
      return
    }
    if (h.avisos.length > 0) {
      setEscolhido(h)
      return
    }
    void enviar({ slot_id: h.slot_id })
  }

  const fixada = opcoes?.aula.fixada ?? aula.fixada

  return (
    <Painel
      titulo={`${aula.disciplina ?? 'Aula'} · ${aula.turma ?? ''}`}
      nota="Escolha para onde ela vai. A aula movida fica travada — a próxima rodada não a tira do lugar."
      aoFechar={aoFechar}
    >
      <div className="stack">
        <div className="row mover__acoes">
          <span className="field__hint">
            {opcoes?.aula.na_cesta
              ? 'Na cesta: fora da disponibilidade, em negociação.'
              : fixada
                ? 'Travada.'
                : 'Livre: o motor pode mudá-la de lugar.'}
          </span>
          <button
            type="button"
            className="btn btn--secondary"
            disabled={enviando}
            onClick={() => void enviar({ fixada: !fixada })}
          >
            {fixada ? 'Destravar' : 'Travar aqui'}
          </button>
        </div>

        {erro && (
          <p className="state state--pendencia" role="alert">
            <Icon name="aviso" size={14} />
            {erro}
          </p>
        )}

        {opcoes && (
          <div className="tabela-rolavel">
            <table className="tabela mover__grade" data-tour="mover">
              <thead>
                <tr>
                  <th />
                  {dias.map((d) => (
                    <th key={d}>{DIA[d]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inicios.map((i) => (
                  <tr key={i}>
                    <th scope="row" className="mono">
                      {hora(i)}
                    </th>
                    {dias.map((d) => {
                      const h = em(d, i)
                      if (!h) return <td key={d} />
                      const duro = h.avisos.some((a) => a.duro)
                      const estado = h.atual ? 'atual' : duro ? 'bloqueado' : h.avisos.length ? 'aviso' : 'livre'
                      return (
                        <td key={d}>
                          <button
                            type="button"
                            className={`mover__opcao mover__opcao--${estado}`}
                            aria-pressed={escolhido?.slot_id === h.slot_id}
                            title={h.avisos.map((a) => a.mensagem).join(' ') || (h.atual ? 'Está aqui agora' : 'Livre')}
                            disabled={enviando || h.atual}
                            onClick={() => clicar(h)}
                          >
                            {estado === 'atual'
                              ? 'aqui'
                              : estado === 'bloqueado'
                                ? 'ocupado'
                                : estado === 'aviso'
                                  ? 'com aviso'
                                  : 'livre'}
                          </button>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {escolhido && (
          <div className="callout stack" role="group" aria-label="Pôr na cesta">
            <p>
              <strong>
                {DIA[escolhido.dia]} {hora(escolhido.inicio)}:
              </strong>{' '}
              {escolhido.avisos.map((a) => a.mensagem).join(' ')}
            </p>
            <p className="field__hint">
              Dá para pôr a aula aqui mesmo assim — ela fica "na cesta", e abre uma pendência em
              negociação com o motivo abaixo como primeira nota.
            </p>
            <label className="field">
              <span className="field__label">Motivo</span>
              <input
                className="input"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="vou negociar a terça com a professora"
              />
            </label>
            <div className="row">
              <button
                type="button"
                className="btn btn--primary"
                disabled={!motivo.trim() || enviando}
                onClick={() => void enviar({ slot_id: escolhido.slot_id, confirmar: true, motivo: motivo.trim() })}
              >
                Pôr na cesta
              </button>
              <button type="button" className="btn btn--secondary" onClick={() => setEscolhido(null)}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    </Painel>
  )
}
